import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, FormButtonStyled, HeadingStyled, TextStyled, ThemeLoader } from "../../../../components/styled/Elements";
import { toast } from "react-toastify";
import AuthApi from "../../../../services/api/authApi";

const MOYASAR_PUBLIC_KEY = process.env.REACT_APP_MOYASAR_PUBLIC_KEY;
const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SignupStep3 = ({
  paymentMethod,
  bankInfo,
  planInfo,
  addLicenseFrm,
  watch,
  totalAmount,
  user,
  validateCurrentStep,
  promoDiscount,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const promoCode = watch("promotion_code");
  const numOfLicenses = watch("number_of_licenses");

  const [submitLoading, setSubmitLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [moyasarForm, setMoyasarForm] = useState(null);

  // Helper to build payload so it's consistent across both methods
  const getPayloadData = () => {
    const baseCost = parseFloat(planInfo?.amount) || 0;
    const discount = baseCost * (parseFloat(promoDiscount) / 100);
    const subCost = baseCost - (discount || 0);

    return {
      user_id: user?.id,
      plan_id: planInfo?.id,
      plan_duration: planInfo?.duration || 1,
      price_per_license: planInfo?.amount,
      total_amount: totalAmount,
      payment_method: paymentMethod,
      number_of_licenses: numOfLicenses,
      sub_total: baseCost,
      vat_percentage: bankInfo?.vat?.vat || 0,
      vat_value: bankInfo?.vat?.vat ? (parseFloat(bankInfo?.vat?.vat) / 100) * subCost : 0,
      discount_percentage: promoDiscount || 0,
      discount_value: discount || 0,
      promotion_code: promoCode || "",
    };
  };

  // Helper to convert object to FormData for your backend calls
  const convertToFormData = (data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });
    return formData;
  };

  const payloadData = getPayloadData();
  // ============================================================================
  // INITIALIZE MOYASAR FORM
  // ============================================================================
  useEffect(() => {
    if (paymentMethod !== "card") {
      setFormLoading(false);
      return;
    }

    const initMoyasar = () => {
      if (!window.Moyasar || !formRef.current) return;

      try {
        formRef.current.innerHTML = "";

        const form = window.Moyasar.init({
          element: formRef.current,
          amount: Math.round(totalAmount * 100), // Convert SAR to halalas
          currency: "SAR",
          description: `Plan Subscription - ${planInfo?.title || "Plan"} (User: ${user?.id})`,
          publishable_api_key: MOYASAR_PUBLIC_KEY,
          // Moyasar redirects here natively after card auth processing
          callback_url: `${REACT_APP_API_BASE_URL}/authentication/moyasar-callback`,
          methods: ['creditcard'],
          supported_networks: ['mada', 'visa', 'mastercard', 'amex'],
          metadata: payloadData,
          on_completed: async function (payment) {
            try {
              // 1. Generate your standard step-two form data payload
              const formData = convertToFormData(payloadData);

              // 2. Append the new Moyasar ID details so the backend can log it
              formData.append("moyasar_payment_id", payment.id);
              formData.append("moyasar_status", payment.status);

              // 3. Fire the request exactly like the free tier flow does
              const { code, message } = await AuthApi.signupStepTwo(formData);
              
              if (code !== 200) {
                toast.error(message || "Registration sync failed. Payment halted.");
                throw new Error("Backend Sync Failed");
              }
            } catch (error) {
              console.error("Card signup validation sync error:", error);
              toast.error("Could not sync transaction details. Please try again.");
              throw error; 
            }
          }
        });

        setMoyasarForm(form);
        setFormLoading(false);
      } catch (error) {
        console.error("Failed to initialize Moyasar:", error);
        toast.error("Failed to load payment form. Please try again.");
        setFormLoading(false);
      }
    };

    const script = document.querySelector('script[src*="moyasar"]');

    if (window.Moyasar) {
      initMoyasar();
    } else if (script) {
      const handleScriptLoad = () => initMoyasar();
      script.addEventListener('load', handleScriptLoad);
      return () => script.removeEventListener('load', handleScriptLoad);
    } else {
      console.error("Moyasar script tag missing from index.html");
      toast.error("Payment integration script missing.");
      setFormLoading(false);
    }

    return () => {
      if (moyasarForm && typeof moyasarForm.unmount === "function") {
        moyasarForm.unmount();
      }
    };
  }, [paymentMethod, totalAmount, planInfo, user?.id]);

  // ============================================================================
  // SUBMIT FLOWS
  // ============================================================================
  const handleSubmit = async () => {
    const isCurrentStepValid = await validateCurrentStep();
    if (!isCurrentStepValid) return;

    setSubmitLoading(true);
    const formData = convertToFormData(payloadData);
    
    // 1. Handle Free Payment Method
    if (paymentMethod === "free") {
      try {
        const { code, message } = await AuthApi.signupStepTwo(formData);
        if (code === 200) {
          toast.success(message);
          navigate("/login");
        } else {
          toast.error(message);
        }
      } catch (error) {
        console.error("Free registration error:", error);
        toast.error("An error occurred. Please try again.");
      } finally {
        setSubmitLoading(false);
      }
      return;
    }

    // 2. Handle Card Payment Method
    if (paymentMethod === "card") {
      if (!moyasarForm) {
        toast.error("Payment form not ready. Please refresh the page.");
        setSubmitLoading(false);
        return;
      }

      try {
        moyasarForm.submit();
      } catch (error) {
        console.error("Card signup step 2 server error:", error);
        toast.error("Server synchronization failed. Please try again.");
        setSubmitLoading(false);
      }
    }
  };

  // useEffect(() => {
  //   const updateButtonText = () => {
  //     const mysrPayBtn = document.querySelector('#mysr button[type="submit"]');
  //     if (mysrPayBtn) {
  //       mysrPayBtn.textContent = "Pay & Continue";
  //     }
  //   };

  //   // Run immediately
  //   updateButtonText();

  //   // If the form takes a second to render, a small timeout ensures we catch it
  //   const timer = setTimeout(updateButtonText, 500);
  //   return () => clearTimeout(timer);
  // }, [moyasarForm]);

  return (
    <Step3Wrapper>
      <Box>
        {paymentMethod === "card" && (
          <>
            {formLoading && <ThemeLoader />}
            <Box style={{ display: formLoading ? "none" : "block" }}>
              <Box className="mb-4">
                <HeadingStyled size="24px" weight="bold" color="var(--text)">
                  {t("pay_through_card")}
                </HeadingStyled>
                <TextStyled size="16px" color="var(--text2)">
                  {t("please_enter_card_info_bellow")}
                </TextStyled>
              </Box>

              <Box ref={formRef} className="mb-4 mysr-form"></Box>

              <TextStyled size="12px" color="var(--text2)" className="mb-3">
                🔒 {t("secure_payment")} - Your card information is encrypted and never stored on our servers.
              </TextStyled>
            </Box>
          </>
        )}

        {paymentMethod === "free" && (
          <Box className="mb-4">
            <HeadingStyled size="24px" weight="bold" color="var(--text)">
              {t("free_plan")}
            </HeadingStyled>
            <TextStyled size="16px" color="var(--text2)">
              {t("no_payment_required")}
            </TextStyled>
          </Box>
        )}
      </Box>

      {/* <FormButtonStyled
        label={addLicenseFrm ? t("Submit") : t("sign_up")}
        color="var(--white)"
        bg="var(--themeColor)"
        className="w-100"
        clName="w-100 my-4"
        isLoading={submitLoading || (paymentMethod === "card" && formLoading)}
        disabled={submitLoading || (paymentMethod === "card" && formLoading)}
        onClick={handleSubmit}
        type="submit"
      /> */}
    </Step3Wrapper>
  );
};

export default SignupStep3;

const Step3Wrapper = styled.div`
  #card-footer { display: none; }
  .mysr-form {
    border-radius: 8px;
    padding: 16px;
    background-color: var(--white);
    min-height: 200px;
  }
  @media (max-width: 768px) {
    .mysr-form { padding: 12px; }
  }
`;